import { cn } from "@/lib/utils";
import { CheckCircle2 } from "lucide-react";

interface AnimatedSectionBadgeProps {
  number: number;
  title?: string;
  isComplete?: boolean;
  isActive?: boolean;
}

export const AnimatedSectionBadge = ({ 
  number, 
  title, 
  isComplete = false,
  isActive = false 
}: AnimatedSectionBadgeProps) => {
  return (
    <div className={cn(
      "flex items-center gap-3 mb-4 md:mb-6",
      "animate-fade-in"
    )}>
      {/* Number badge */}
      <div className={cn(
        "relative w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center",
        "font-bold text-sm md:text-base",
        "transition-all duration-500",
        isComplete
          ? "bg-neon-green/20 text-neon-green border border-neon-green/30 shadow-[0_0_15px_hsl(var(--neon-green)/0.3)]"
          : isActive
            ? "bg-gradient-to-br from-neon-purple to-neon-pink text-white shadow-[0_0_20px_hsl(var(--neon-purple)/0.4)]"
            : "bg-gradient-to-br from-neon-purple/20 to-neon-pink/20 text-neon-purple border border-neon-purple/30"
      )}>
        {isComplete ? (
          <CheckCircle2 className="w-5 h-5 animate-check-pop" />
        ) : (
          <span>{number}</span>
        )}

        {/* Active pulse ring */}
        {isActive && !isComplete && (
          <div className="absolute inset-0 rounded-xl border-2 border-neon-purple/50 animate-ping opacity-50" />
        )}

        {/* Completion particles */}
        {isComplete && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-neon-green animate-particle-burst"
                style={{
                  left: '50%',
                  top: '50%',
                  '--angle': `${i * 60}deg`,
                } as React.CSSProperties}
              />
            ))}
          </div>
        )}
      </div>

      {/* Title (optional) */}
      {title && (
        <h2 className={cn(
          "text-lg md:text-xl font-bold transition-colors duration-300",
          isComplete && "text-neon-green",
          isActive && !isComplete && "text-foreground"
        )}>
          {title}
        </h2>
      )}
    </div>
  );
};
